/**
 * Serviço centralizado para salvamento de arquivos no CorelDRAW
 * @module SaveService
 * 
 * ⚠️ IMPORTANTE: salvararquivo usa payloads grandes, HMAC é opcional
 * Para a maioria dos casos, use sendMessage (sem HMAC) para melhor performance
 */

import { corelCommunication } from '../communication';
import { debugLog, sendToTerminal, terminalLog } from '../../terminalLogger';
import { RequestService } from './request';
import { actionDebugger } from './actionDebugger';
import { getActionTimeout } from '../timeoutService';
import type {
  SaveFileOptions,
  SaveFileResult,
  SaveFileSafeOptions,
  SaveMetaDetails,
  IntegrityInfo,
  CorelSavePayload,
  SmbCredentials
} from './save.types';

/**
 * Erros padronizados
 */
const ERROR_NO_SAVE_DATA = 'SAVE_ERROR:VALIDAÇÃO: Dados de salvamento não encontrados';
const ERROR_NO_PATH_RETURNED = 'SAVE_ERROR:VALIDAÇÃO: C# não retornou CaminhoSalvo';
const ERROR_MISSING_REQUIRED_FIELD = (field: string) =>
  `SAVE_ERROR:VALIDAÇÃO: Campo obrigatório ausente: ${field}`;

/**
 * Serviço centralizado para salvamento de arquivos
 */
export class SaveService {
  /**
   * Salva arquivo no CorelDRAW
   * 
   * ⚠️ Por padrão usa sendMessage (sem HMAC) devido ao tamanho do payload
   * Configure useHmac=true se necessário validação HMAC
   * 
   * @param options Opções de salvamento
   * @returns Promise com resultado
   */
  static async saveFile(options: SaveFileOptions): Promise<SaveFileResult> {
    const startTime = Date.now();
    const {
      workingData,
      metadata,
      role,
      useHmac = true,  // ✅ CORREÇÃO: Backend EXIGE HMAC para salvararquivo
      timeoutMs = getActionTimeout('salvararquivo'),  // timeout do painel admin (fallback: 1h)
      onProgress,
      onSuccess,
      onError,
      notify
    } = options;

    const correlationId = workingData?.pedido?.orc_id || `save-${Date.now()}`;

    sendToTerminal('🔵 [SAVE] Iniciando saveFile', {
      pedidoId: workingData?.pedido?.orc_id,
      itemId: workingData?.pedido?.oit_id,
      useHmac,
      timeoutMs,
      correlationId
    });

    try {
      // 1. VALIDAÇÃO INICIAL
      if (!workingData) {
        const error = ERROR_NO_SAVE_DATA;
        sendToTerminal('❌ [SAVE] ' + error, { correlationId });
        if (onError) onError(error);
        return { success: false, error, elapsedMs: Date.now() - startTime };
      }

      // 2. VALIDAÇÕES CRÍTICAS
      const validation = this.validatePayload(workingData);
      if (!validation.ok) {
        const error = `SAVE_ERROR:VALIDAÇÃO: ${validation.missing.join('; ')}`;
        sendToTerminal('❌ [SAVE] Validações falharam', {
          errors: validation.missing,
          totalErrors: validation.missing.length,
          correlationId
        });
        if (onError) onError(error);
        return { success: false, error, elapsedMs: Date.now() - startTime };
      }

      // 3. PREPARAR CREDENCIAIS SMB
      if (onProgress) onProgress('prepare_smb');
      const smbPayload = await this.prepareSmbCredentials(workingData, notify);
      if (smbPayload === null) {
        // Falha crítica nas credenciais
        const error = 'SAVE_ERROR:SMB_FETCH_FAILED: Erro ao obter credenciais de rede';
        sendToTerminal('❌ [SAVE] Erro ao obter credenciais SMB', { correlationId });
        if (onError) onError(error);
        return { success: false, error, elapsedMs: Date.now() - startTime };
      }

      // 4. CONSTRUIR PAYLOAD
      if (onProgress) onProgress('build_payload');
      const payload = this.buildPayload(workingData, metadata, role, smbPayload);

      sendToTerminal('ℹ️ [SAVE] Payload construído', {
        pedidoId: payload.pedidoId,
        itemId: payload.itemId,
        objetosCount: metadata.objetos.length,
        useHmac,
        correlationId
      });

      // 5. ENVIAR PARA COREL
      if (onProgress) onProgress('send_to_corel');

      return new Promise((resolve) => {
        const sendMethod = useHmac
          ? corelCommunication.sendActionGuarded.bind(corelCommunication)
          : corelCommunication.sendMessage.bind(corelCommunication);

        const timeoutId = setTimeout(() => {
          const error = 'Timeout ao salvar arquivo (excedeu ' + (timeoutMs / 1000) + 's)';
          sendToTerminal('⏱️ [SAVE] ' + error);
          if (onError) onError(error);
          resolve({
            success: false,
            error,
            elapsedMs: Date.now() - startTime
          });
        }, timeoutMs);

        try {
          const requestId = sendMethod(
            'salvararquivo',
            payload as any,
            (response: any) => {
              clearTimeout(timeoutId);
              if (onProgress) onProgress('process_response');

              const result = this.processResponse(
                response,
                metadata,
                startTime,
                String(correlationId)
              );

              if (result.success) {
                sendToTerminal('✅ [SAVE] saveFile concluído', {
                  correlationId,
                  elapsedMs: result.elapsedMs,
                  savedPath: result.caminhoSalvo
                });
                if (onSuccess) onSuccess(result);
              } else {
                sendToTerminal('❌ [SAVE] saveFile falhou', {
                  correlationId,
                  elapsedMs: result.elapsedMs,
                  error: result.error
                });
                if (onError) onError(result.error || 'Erro desconhecido');
              }

              resolve(result);
            },
            useHmac ? timeoutMs : undefined
          );

          if (!requestId) {
            clearTimeout(timeoutId);
            const error = 'Falha ao enviar comando salvararquivo';
            sendToTerminal('❌ [SAVE] ' + error, { correlationId, elapsedMs: Date.now() - startTime });
            if (onError) onError(error);
            resolve({
              success: false,
              error,
              elapsedMs: Date.now() - startTime
            });
          }
        } catch (error) {
          clearTimeout(timeoutId);
          const errorMsg = error instanceof Error ? error.message : 'Erro desconhecido';
          sendToTerminal('❌ [SAVE] Exceção ao enviar', {
            error: errorMsg,
            correlationId,
            elapsedMs: Date.now() - startTime
          });
          if (onError) onError(errorMsg);
          resolve({
            success: false,
            error: errorMsg,
            elapsedMs: Date.now() - startTime
          });
        }
      });

    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Erro desconhecido';
      sendToTerminal('❌ [SAVE] Exceção ao salvar', {
        error: errorMsg,
        correlationId,
        elapsedMs: Date.now() - startTime
      });
      if (onError) onError(errorMsg);
      return {
        success: false,
        error: errorMsg,
        elapsedMs: Date.now() - startTime
      };
    }
  }

  /**
   * Salva arquivo com retry automático
   * 
   * @param options Opções de salvamento + retry
   * @returns Promise com resultado
   */
  static async saveFileSafe(options: SaveFileSafeOptions): Promise<SaveFileResult> {
    const maxRetries = options.maxRetries ?? 1;
    const retryDelayMs = options.retryDelayMs ?? 1000;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        const result = await this.saveFile(options);

        if (result.success) {
          return result;
        }

        // Se não for a última tentativa, aguardar antes de retry
        if (attempt < maxRetries) {
          sendToTerminal(`🔄 [SAVE] Tentativa ${attempt}/${maxRetries} falhou, retrying...`);
          await new Promise(r => setTimeout(r, retryDelayMs));
        } else {
          return result; // Última tentativa falhou
        }
      } catch (error) {
        const errorMsg = error instanceof Error ? error.message : String(error);
        sendToTerminal(`⚠️ [SAVE] Tentativa ${attempt}/${maxRetries} com exceção`, {
          error: errorMsg
        });

        if (attempt >= maxRetries) {
          return {
            success: false,
            error: errorMsg,
            elapsedMs: 0
          };
        }

        await new Promise(r => setTimeout(r, retryDelayMs));
      }
    }

    return {
      success: false,
      error: 'Todas as tentativas de salvamento falharam',
      elapsedMs: 0
    };
  }

  /**
   * Cancela operação de salvamento em andamento
   * 
   * @param requestId ID da requisição
   * @returns Promise<boolean>
   */
  static async cancelSave(requestId: string): Promise<boolean> {
    sendToTerminal('🔵 [SAVE] Iniciando cancelSave', { requestId });

    try {
      actionDebugger.logActionCall('SaveService.cancelSave', 'RequestService.cancel', { requestId }, { requestId });
      await RequestService.cancel(requestId);

      sendToTerminal('✅ [SAVE] cancelSave concluído', { requestId });
      return true;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      sendToTerminal('❌ [SAVE] Erro em cancelSave', {
        requestId,
        error: errorMessage
      });
      return false;
    }
  }

  /**
   * Valida payload de salvamento
   * 
   * @param workingData Dados de salvamento
   * @returns Resultado da validação
   */
  private static validatePayload(workingData: any): { ok: boolean; missing: string[] } {
    const missing: string[] = [];

    // Campos obrigatórios
    if (!workingData.caminho_arquivo) missing.push('caminho_arquivo');
    if (!workingData.pedido?.orc_id) missing.push('pedido.orc_id');
    if (!workingData.pedido?.oit_id) missing.push('pedido.oit_id');

    return {
      ok: missing.length === 0,
      missing
    };
  }

  /**
   * Prepara credenciais SMB
   * 
   * @param workingData Dados de salvamento
   * @param notify Função de notificação
   * @returns Credenciais SMB ou undefined/null
   */
  private static async prepareSmbCredentials(
    workingData: any,
    notify?: SaveFileOptions['notify']
  ): Promise<SmbCredentials | undefined | null> {
    try {
      const { prepareSmbCredentials } = await import('../../../utils/smbCredentialsHelper');

      const smbResult = await prepareSmbCredentials({
        source: 'rules',
        rules: workingData.regras,
        path: workingData.caminho_arquivo,
        context: 'SAVE',
        forceSmb: true,
        maxRetries: 3,
        retryDelay: 500
      });

      if (!smbResult.success) {
        const error = `SMB_FETCH_FAILED: ${smbResult.error || 'Erro ao obter credenciais'}`;
        sendToTerminal('❌ [SAVE] ' + error, {
          errorType: smbResult.errorType,
          caminho: workingData.caminho_arquivo
        });

        if (notify) {
          notify({
            type: 'error',
            title: 'Erro de Credenciais de Rede',
            message: `Não foi possível obter credenciais. ${smbResult.error || 'Tente novamente.'}`,
            sticky: true,
          });
        }

        return null; // Crítico
      }

      if (smbResult.hasCredentials && smbResult.credentials) {
        return {
          username: smbResult.credentials.username,
          password: smbResult.credentials.password,
          ...(smbResult.credentials.domain ? { domain: smbResult.credentials.domain } : {})
        };
      }

      return undefined; // Sem credenciais
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : String(error);
      sendToTerminal('❌ [SAVE] Exceção ao preparar SMB', {
        error: errorMsg,
        caminho: workingData.caminho_arquivo
      });
      return undefined;
    }
  }

  /**
   * Constrói payload para o CorelDRAW
   * 
   * @param workingData Dados de salvamento
   * @param metadata Metadata
   * @param role Role do usuário
   * @param smbPayload Credenciais SMB
   * @returns Payload completo
   */
  private static buildPayload(
    workingData: any,
    metadata: any,
    role?: string,
    smbPayload?: SmbCredentials
  ): CorelSavePayload {
    const caminhoCompleto = workingData.caminho_arquivo || '';
    const nomeArquivo = caminhoCompleto ? caminhoCompleto.split('\\').pop() || '' : '';

    // ✅ CORREÇÃO FINAL: C# espera diretório COMPLETO onde o arquivo será salvo
    // Extrair do caminho completo (remove o nome do arquivo)
    const diretorioCompleto = caminhoCompleto.includes('\\')
      ? caminhoCompleto.substring(0, caminhoCompleto.lastIndexOf('\\'))
      : '';

    const idempotencyKey = `salvar-${workingData.pedido?.orc_id ?? '0'}-${workingData.pedido?.oit_id ?? '0'}`;

    return {
      caminho: workingData.caminho_arquivo,
      nomeArquivo: nomeArquivo || workingData?.nome_arquivo || metadata.documento?.arquivo || '',
      // Usar diretório completo (fallback para diretorios.interno se não conseguir extrair)
      diretorio: diretorioCompleto || workingData.diretorios?.interno || '',
      substituirSeExistir: true,
      pedidoId: workingData.pedido?.orc_id,
      itemId: workingData.pedido?.oit_id,
      cliente: workingData.cliente?.nome_fantasia || workingData.cliente?.nome || '',
      produto: workingData.produto?.nome || '',
      entrega: workingData.configuracoes?.entrega || 'Hoje',
      usuario: workingData.usuario || role || 'usuario',
      timestamp: new Date().toISOString(),
      padroesCdr: {
        versao_arquivo: workingData.regras?.padroes_cdr?.versao_arquivo || '2021',
        perfil_icc: workingData.regras?.padroes_cdr?.perfil_icc || 'sRGB',
        compactar: workingData.regras?.padroes_cdr?.compactar ?? true,
        salvar_com_visualizacao: workingData.regras?.padroes_cdr?.salvar_com_visualizacao ?? true,
      },
      metadata,
      responseMode: 'minimal',
      idempotencyKey,
      ...(smbPayload ? { smb: smbPayload } : {})
    };
  }

  /**
   * Processa resposta do CorelDRAW
   * 
   * @param response Resposta
   * @param metadata Metadata enviada
   * @param startTime Timestamp de início
   * @returns Resultado processado
   */
  private static processResponse(
    response: any,
    metadata: any,
    startTime: number,
    correlationId?: string
  ): SaveFileResult {
    const elapsedMs = Date.now() - startTime;
    const status = response?.status;

    // Divergência detectada
    if (status === 'divergencia_detectada') {
      const divergencias = response?.divergencias || [];
      sendToTerminal('⚠️ [SAVE] Divergências detectadas', {
        count: divergencias.length,
        correlationId
      });
      return {
        success: false,
        error: `Divergências detectadas: ${divergencias.length} item(ns)`,
        elapsedMs
      };
    }

    // Sucesso
    if (status === 'ok') {
      const result = response?.result || {};
      const caminhoSalvo = result.caminhoSalvo || result.CaminhoSalvo;

      if (!caminhoSalvo) {
        sendToTerminal('❌ [SAVE] ' + ERROR_NO_PATH_RETURNED, { correlationId });
        return {
          success: false,
          error: ERROR_NO_PATH_RETURNED,
          elapsedMs
        };
      }

      // Extrair integridade
      const integridade = this.extractIntegrity(result, metadata);

      // Construir metadados detalhados
      const metadataDetails: SaveMetaDetails = {
        integridade: integridade.detailed,
        hashAlgoritmo: result.hashAlgoritmo || result.HashAlgoritmo || '',
        hashArquivo: result.hashArquivo || result.HashArquivo || '',
        tamanhoBytes: result.tamanhoBytes || result.TamanhoBytes || 0,
        ultimaGravacaoUtc: result.snapshot?.documento?.timestamp ||
          result.ultimaGravacaoUtc ||
          result.UltimaGravacaoUtc ||
          new Date().toISOString(),
        objetos: result.snapshot?.objetos || result.objetos || result.Objetos || metadata.objetos,
        payload_snapshot: response || {}
      };

      sendToTerminal('✅ [SAVE] Arquivo salvo com sucesso', {
        caminho: caminhoSalvo,
        integridade: integridade.summary,
        elapsedMs,
        correlationId
      });

      if (integridade.summary.metadadosPerdidos > 0) {
        terminalLog.warn('[SAVE] Metadados perdidos', {
          count: integridade.summary.metadadosPerdidos
        });
      }

      return {
        success: true,
        caminhoSalvo,
        integridade: integridade.summary,
        metadata: metadataDetails,
        elapsedMs,
        metrics: result.metrics
      };
    }

    // Erro
    const errorMsg = response?.message || response?.error || 'Erro ao salvar arquivo';
    sendToTerminal('❌ [SAVE] Erro', { error: errorMsg, status, correlationId });
    return {
      success: false,
      error: errorMsg,
      elapsedMs
    };
  }

  /**
   * Extrai informações de integridade
   * 
   * @param result Resultado do Corel
   * @param metadata Metadata enviada
   * @returns Informações de integridade
   */
  private static extractIntegrity(result: any, metadata: any): {
    summary: IntegrityInfo;
    detailed: any;
  } {
    // Formato 1: Com snapshot (NOVO)
    if (result.snapshot) {
      const objetosCount = result.snapshot.objetos?.length ?? 0;
      return {
        summary: {
          objetosVerificados: objetosCount,
          metadadosOk: objetosCount,
          metadadosPerdidos: 0
        },
        detailed: {
          objetosVerificados: objetosCount,
          metadadosOk: objetosCount,
          metadadosPerdidos: 0
        }
      };
    }

    // Formato 2: Com Integridade (LEGADO)
    if (result.Integridade) {
      return {
        summary: {
          objetosVerificados: result.Integridade?.validacaoPosSalvamento?.objetosVerificados ?? 0,
          metadadosOk: result.Integridade?.validacaoPosSalvamento?.metadadosOk ?? 0,
          metadadosPerdidos: result.Integridade?.validacaoPosSalvamento?.metadadosPerdidos ?? 0
        },
        detailed: {
          objetosVerificados: result.Integridade?.validacaoPosSalvamento?.objetosVerificados ?? 0,
          metadadosOk: result.Integridade?.validacaoPosSalvamento?.metadadosOk ?? 0,
          metadadosPerdidos: result.Integridade?.validacaoPosSalvamento?.metadadosPerdidos ?? 0,
          objetosProcessados: result.Integridade?.objetosProcessados,
          guidsCriados: result.Integridade?.guidsCriados,
          guidsDuplicadosCorrigidos: result.Integridade?.guidsDuplicadosCorrigidos,
          metadadosReparados: result.Integridade?.metadadosReparados,
          faltantesAntes: result.Integridade?.faltantesAntes,
          faltantesDepois: result.Integridade?.faltantesDepois
        }
      };
    }

    // Formato 3: Sem integridade (FALLBACK)
    terminalLog.warn('[SAVE] C# não retornou integridade nem snapshot');
    const objetosCount = metadata.objetos.length;
    return {
      summary: {
        objetosVerificados: objetosCount,
        metadadosOk: objetosCount,
        metadadosPerdidos: 0
      },
      detailed: {
        objetosVerificados: objetosCount,
        metadadosOk: objetosCount,
        metadadosPerdidos: 0
      }
    };
  }
}

/**
 * Função auxiliar para compatibilidade
 * @deprecated Use SaveService.saveFile() diretamente
 */
export const saveFile = SaveService.saveFile;

